import { z } from 'zod'
import type { PlanChangeOpInput } from './plan-proposals'

/**
 * Plans tout prêts : un type de logement (studio, appartement, maison de plain-pied ou à étage), un nombre de
 * chambres, une surface (proposée, modifiable) et des options donnent des pièces rectangulaires bien rangées, et
 * des meubles du catalogue s'il faut meubler. Le résultat est une **proposition de plan** ordinaire (aperçu,
 * « Appliquer ») : rien n'est écrit avant l'accord. Les murs et les portes intérieures sont déduits des pièces.
 *
 * Mise en page : deux rangées par niveau (jour en haut, nuit et services en bas), étirées à la même largeur, puis
 * mises à l'échelle pour atteindre la surface voulue. Repère du lieu : mètres, x vers la droite, z vers le bas.
 */

export const HOME_KINDS = ['studio', 'apartment', 'single_storey', 'two_storey'] as const
export type HomeKind = (typeof HOME_KINDS)[number]

export const HomeTemplateParamsSchema = z.object({
  kind: z.enum(HOME_KINDS),
  bedrooms: z.number().int().min(0).max(6),
  /** Surface habitable voulue (m²) ; absente : celle proposée pour ce logement (`suggestedArea`). */
  area: z.number().min(15).max(400).optional(),
  kitchen: z.enum(['open', 'closed']),
  office: z.boolean().default(false),
  storage: z.boolean().default(false),
  laundry: z.boolean().default(false),
  secondBathroom: z.boolean().default(false),
  /** Balcon (appartement) ou terrasse (maison), le long du séjour. */
  outdoor: z.boolean().default(false),
  /** Garage collé à la maison, ou bâtiment à part sur le terrain. */
  garage: z.enum(['none', 'attached', 'separate']).default('none'),
  /** Meubler avec l'essentiel (meubles du catalogue, contre les murs). */
  furnish: z.boolean().default(true),
})
export type HomeTemplateParams = z.infer<typeof HomeTemplateParamsSchema>
export type HomeTemplateParamsInput = z.input<typeof HomeTemplateParamsSchema>

/** Points de départ du catalogue (tout reste réglable ensuite). */
export const HOME_PRESETS: { id: string; params: HomeTemplateParamsInput }[] = [
  { id: 'studio', params: { kind: 'studio', bedrooms: 0, kitchen: 'open' } },
  { id: 't2', params: { kind: 'apartment', bedrooms: 1, kitchen: 'open' } },
  { id: 't3', params: { kind: 'apartment', bedrooms: 2, kitchen: 'closed', storage: true } },
  { id: 't4', params: { kind: 'apartment', bedrooms: 3, kitchen: 'open', storage: true, outdoor: true } },
  { id: 'single_storey', params: { kind: 'single_storey', bedrooms: 3, kitchen: 'open', laundry: true, garage: 'attached', outdoor: true } },
  { id: 'two_storey', params: { kind: 'two_storey', bedrooms: 4, kitchen: 'open', laundry: true, secondBathroom: true, outdoor: true } },
  { id: 'with_land', params: { kind: 'single_storey', bedrooms: 2, kitchen: 'closed', office: true, garage: 'separate', outdoor: true } },
]

type Translate = (key: string, opts?: Record<string, unknown>) => string
type RoomKind = 'day' | 'night' | 'wet' | 'service' | 'outdoor' | 'garage'
type Slot = { key: string; name: string; kind: RoomKind; w: number; grow?: boolean; furniture?: Furnish[] }
type Furnish = { catalog: string; where: 'wall' | 'center' | 'end'; rotationY?: number }

export type GeneratedRoom = { ref: string; name: string; kind: RoomKind; floor: number; x: number; z: number; w: number; d: number; row: number }
export type GeneratedPlan = {
  rooms: GeneratedRoom[]
  furniture: { room: string; catalog: string; at: { x: number; z: number }; snapToWall: boolean; rotationY?: number }[]
  building: { name: string; footprint: { x: number; z: number; width: number; depth: number }; room: { name: string; w: number; d: number } } | null
  /** Surface habitable obtenue (sans garage, balcon ni terrasse), arrondie au m². */
  area: number
  floors: number[]
}

const r2 = (n: number) => Math.round(n * 100) / 100

/** Surface proposée pour un logement (m²), d'après ses pièces : celle d'un logement courant de ce type. */
export function suggestedArea(input: HomeTemplateParamsInput): number {
  const p = HomeTemplateParamsSchema.parse({ ...input, area: undefined })
  const plan = layout(p, (k) => k)
  return plan.naturalArea
}

/** Plan d'un logement, posé à partir du point `origin` du lieu (coin haut-gauche). */
export function generateHomePlan(input: HomeTemplateParamsInput, t: Translate, origin = { x: 0, z: 0 }): GeneratedPlan {
  const p = HomeTemplateParamsSchema.parse(input)
  const base = layout(p, t)
  const target = p.area ?? base.naturalArea
  // Mise à l'échelle (bornée) : les proportions restent celles d'un logement courant.
  const f = Math.min(1.6, Math.max(0.7, Math.sqrt(target / base.naturalArea)))
  const scale = (n: number) => r2(n * f)
  const rooms = base.rooms.map((r) => ({
    ...r,
    x: r2(origin.x + (r.kind === 'garage' ? base.garageX * f : r.x * f)),
    z: r2(origin.z + r.z * f),
    w: r.kind === 'garage' ? r.w : scale(r.w),
    d: r.kind === 'outdoor' ? r.d : scale(r.d),
  }))
  // Balcon / terrasse : profondeur fixe, collé au-dessus de la rangée du jour.
  for (const r of rooms) if (r.kind === 'outdoor') r.z = r2(origin.z - r.d)
  const furniture = base.furniture.flatMap((item) => {
    const room = rooms.find((r) => r.ref === item.room)
    if (!room) return []
    // Contre un mur extérieur ou de côté, jamais au milieu d'une cloison entre deux rangées (les portes y sont) :
    // rangée du haut → mur du haut ; dernière rangée → mur du bas ; rangée du milieu → murs de gauche et de droite.
    const cx = room.x + room.w / 2
    const cz = room.z + room.d / 2
    if (item.where === 'center') return [{ room: room.ref, catalog: item.catalog, at: { x: r2(cx - room.w * 0.15), z: r2(room.z + room.d * 0.6) }, snapToWall: false, rotationY: item.rotationY }]
    if (room.row === 1) {
      const x = item.where === 'end' ? room.x + room.w - 0.2 : room.x + 0.2
      return [{ room: room.ref, catalog: item.catalog, at: { x: r2(x), z: r2(cz) }, snapToWall: true }]
    }
    const z = room.row === 0 ? room.z + 0.2 : room.z + room.d - 0.2
    const x = item.where === 'end' ? room.x + room.w - 0.8 : cx
    return [{ room: room.ref, catalog: item.catalog, at: { x: r2(x), z: r2(z) }, snapToWall: true }]
  })
  const width = Math.max(...rooms.filter((r) => r.kind !== 'garage').map((r) => r.x + r.w)) - origin.x
  const building =
    p.garage === 'separate'
      ? { name: t('homeTemplates.rooms.garage'), footprint: { x: r2(origin.x + width + 3), z: origin.z, width: 3.4, depth: 6 }, room: { name: t('homeTemplates.rooms.garage'), w: 3.4, d: 6 } }
      : null
  const area = Math.round(rooms.filter((r) => r.kind !== 'garage' && r.kind !== 'outdoor').reduce((n, r) => n + r.w * r.d, 0))
  return { rooms, furniture: p.furnish ? furniture : [], building, area, floors: [...new Set(rooms.map((r) => r.floor))].sort() }
}

/** Opérations de proposition de plan pour ce plan (pièces, meubles, bâtiment à part). */
export function homePlanOps(plan: GeneratedPlan): PlanChangeOpInput[] {
  return [
    ...plan.rooms.map(
      (r): PlanChangeOpInput => ({
        op: 'create_room',
        ref: r.ref,
        name: r.name,
        floor: r.floor,
        shape: { kind: 'rect', x: r.x, z: r.z, width: r.w, depth: r.d },
        height: r.kind === 'outdoor' ? 1 : 2.5,
        ...(r.kind === 'outdoor' ? { floorStyle: 'plain' as const } : r.kind === 'wet' ? { floorStyle: 'tile' as const } : {}),
      }),
    ),
    ...plan.furniture.map(
      (f): PlanChangeOpInput => ({
        op: 'place_furniture',
        room: f.room,
        from: { catalog: f.catalog },
        at: f.at,
        snapToWall: f.snapToWall,
        ...(f.rotationY !== undefined ? { rotationY: f.rotationY } : {}),
      }),
    ),
    ...(plan.building
      ? [
          {
            op: 'create_building',
            name: plan.building.name,
            footprint: plan.building.footprint,
            rooms: [{ name: plan.building.room.name, shape: { kind: 'rect', x: 0, z: 0, width: plan.building.room.w, depth: plan.building.room.d } }],
          } as PlanChangeOpInput,
        ]
      : []),
  ]
}

// --- Mise en page --------------------------------------------------------------------------------------------

function layout(p: HomeTemplateParams, t: Translate) {
  const name = (key: string, opts?: Record<string, unknown>) => t(`homeTemplates.rooms.${key}`, opts)
  const open = p.kitchen === 'open'
  const n = p.kind === 'studio' ? 0 : Math.max(1, p.bedrooms)
  const house = p.kind === 'single_storey' || p.kind === 'two_storey'

  const bedrooms: Slot[] = Array.from({ length: n }, (_, i) => ({
    key: `bedroom${i + 1}`,
    name: name('bedroom', { n: i + 1 }),
    kind: 'night',
    w: i === 0 ? 3.4 : 3,
    furniture: i === 0 ? [{ catalog: 'bed-160', where: 'wall' }, { catalog: 'wardrobe-2', where: 'end' }] : [{ catalog: 'bed-90', where: 'wall' }, { catalog: 'chest-4', where: 'end' }],
  }))
  const living: Slot[] = open
    ? [{ key: 'living', name: name('livingKitchen'), kind: 'day', w: 6 + n * 0.5, grow: true, furniture: [{ catalog: 'tv-unit', where: 'wall' }, { catalog: 'base-cabinet-120', where: 'end' }, { catalog: 'sofa-3', where: 'center', rotationY: 180 }] }]
    : [
        { key: 'living', name: name('living'), kind: 'day', w: 4 + n * 0.5, grow: true, furniture: [{ catalog: 'tv-unit', where: 'wall' }, { catalog: 'sofa-3', where: 'center', rotationY: 180 }] },
        { key: 'kitchen', name: name('kitchen'), kind: 'day', w: 2.6, furniture: [{ catalog: 'base-cabinet-120', where: 'wall' }, { catalog: 'fridge', where: 'end' }] },
      ]
  const entrance: Slot = { key: 'entrance', name: name('entrance'), kind: 'service', w: 1.8, furniture: [{ catalog: 'shoe-cabinet', where: 'wall' }] }
  const bathroom: Slot = { key: 'bathroom', name: name('bathroom'), kind: 'wet', w: 2.4, furniture: [{ catalog: 'vanity-80', where: 'wall' }] }
  const second: Slot[] = p.secondBathroom ? [{ key: 'bathroom2', name: name('showerRoom'), kind: 'wet', w: 2, furniture: [{ catalog: 'vanity-80', where: 'wall' }] }] : []
  const wc: Slot[] = n >= 2 ? [{ key: 'wc', name: name('wc'), kind: 'wet', w: 1.2 }] : []
  const office: Slot[] = p.office ? [{ key: 'office', name: name('office'), kind: 'night', w: 2.6, furniture: [{ catalog: 'desk', where: 'wall' }, { catalog: 'office-shelf', where: 'end' }] }] : []
  const services: Slot[] = [
    ...(p.storage ? [{ key: 'storage', name: name('storage'), kind: 'service' as const, w: 1.4, furniture: [{ catalog: 'metal-shelf', where: 'wall' as const }] }] : []),
    ...(p.laundry ? [{ key: 'laundry', name: name('laundry'), kind: 'service' as const, w: 1.8, furniture: [{ catalog: 'washing-machine', where: 'wall' as const }] }] : []),
  ]
  if (!p.laundry && n > 0) bathroom.furniture = [...(bathroom.furniture ?? []), { catalog: 'washing-machine', where: 'end' }]

  type Row = { slots: Slot[]; depth: number }
  type Level = { floor: number; rows: Row[] }
  /** Range des pièces en rangées d'au plus `width` mètres, équilibrées (pas de petite rangée restante). */
  const wrap = (slots: Slot[], width: number, depth: number): Row[] => {
    if (!slots.length) return []
    const total = slots.reduce((t, x) => t + x.w, 0)
    // Jusqu'à 30 % plus large que la rangée du jour, une seule rangée (le séjour s'élargit un peu) ; au-delà, découpage.
    const count = Math.max(1, Math.ceil(total / (width * 1.3)))
    const target = total / count
    const rows: Row[] = []
    let current: Slot[] = []
    let sum = 0
    for (const s of slots) {
      if (current.length && sum + s.w / 2 > target && rows.length < count - 1) {
        rows.push({ slots: current, depth })
        current = []
        sum = 0
      }
      current.push(s)
      sum += s.w
    }
    rows.push({ slots: current, depth })
    return rows
  }
  const naturalWidth = (slots: Slot[]) => slots.reduce((t, x) => t + x.w, 0)

  let levels: Level[]
  if (p.kind === 'studio') {
    levels = [
      {
        floor: 0,
        rows: [
          { slots: [{ key: 'living', name: name('studioLiving'), kind: 'day', w: 5.2, grow: true, furniture: [{ catalog: 'bed-140', where: 'wall' }, { catalog: 'base-cabinet-120', where: 'end' }] }], depth: 4 },
          { slots: [entrance, { key: 'bathroom', name: name('showerRoom'), kind: 'wet', w: 2.2, furniture: [{ catalog: 'vanity-80', where: 'wall' }] }, ...office, ...services], depth: 2.2 },
        ],
      },
    ]
  } else if (p.kind === 'two_storey') {
    const width = Math.max(naturalWidth(living), 8)
    levels = [
      {
        floor: 0,
        rows: [
          { slots: living, depth: 4.4 },
          ...wrap([entrance, { key: 'wc', name: name('wc'), kind: 'wet', w: 1.2 }, { key: 'stairs', name: name('stairs'), kind: 'service', w: 2.2 }, ...office, ...services], width, 2.8),
        ],
      },
      { floor: 1, rows: [...wrap(bedrooms, width, 3.4), ...wrap([{ key: 'landing', name: name('landing'), kind: 'service', w: 2.2 }, bathroom, ...second], width, 2.6)] },
    ]
  } else {
    const width = Math.max(naturalWidth(living), 7.5)
    // Petit logement : chambres, entrée et salle de bain sur une seule rangée ; sinon les chambres, puis l'eau et les
    // rangements.
    const nightRows = (w: number): Row[] => {
      const all = [...bedrooms, ...office, entrance, bathroom, ...second, ...wc, ...services]
      return naturalWidth(all) <= w * 1.3
        ? [{ slots: all, depth: 3.3 }]
        : [...wrap([...bedrooms, ...office], w, 3.3), ...wrap([entrance, bathroom, ...second, ...wc, ...services], w, 2.4)]
    }
    levels = [
      {
        floor: 0,
        rows: [{ slots: living, depth: 4.2 }, ...nightRows(width)],
      },
    ]
  }

  // Largeur commune à toutes les rangées (et aux deux niveaux d'une maison à étage) : le surplus va à la pièce
  // qui s'étire (séjour), sinon à la dernière de la rangée.
  const width = Math.max(...levels.flatMap((l) => l.rows.map((r) => r.slots.reduce((s, x) => s + x.w, 0))))
  const lastRow = Math.max(...levels.map((l) => l.rows.length - 1))
  const rooms: GeneratedRoom[] = []
  const furniture: { room: string; catalog: string; where: Furnish['where']; rotationY?: number }[] = []
  for (const level of levels) {
    let z = 0
    level.rows.forEach((row, rowIndex) => {
      const sum = row.slots.reduce((s, x) => s + x.w, 0)
      const extra = width - sum
      const grower = row.slots.find((s) => s.grow)
      let x = 0
      for (const s of row.slots) {
        // Rangée du jour : le surplus va au séjour ; ailleurs, il est réparti entre les pièces.
        const w = grower ? s.w + (s === grower ? extra : 0) : s.w * (width / sum)
        const position = rowIndex === 0 ? 0 : rowIndex === level.rows.length - 1 ? lastRow : 1
        rooms.push({ ref: s.key, name: s.name, kind: s.kind, floor: level.floor, x, z, w, d: row.depth, row: position === lastRow && position > 0 ? 2 : position })
        for (const f of s.furniture ?? []) furniture.push({ room: s.key, ...f })
        x += w
      }
      z += row.depth
    })
  }
  const height = Math.max(...levels.map((l) => l.rows.reduce((s, r) => s + r.depth, 0)))
  if (p.outdoor) {
    const day = rooms.find((r) => r.ref === 'living')!
    rooms.push({ ref: 'outdoor', name: name(house ? 'terrace' : 'balcony'), kind: 'outdoor', floor: 0, x: day.x, z: -1.6, w: day.w, d: house ? 3 : 1.6, row: 0 })
  }
  if (p.garage === 'attached' && p.kind !== 'apartment' && p.kind !== 'studio') {
    rooms.push({ ref: 'garage', name: name('garage'), kind: 'garage', floor: 0, x: width, z: 0, w: 3.4, d: Math.min(6.5, height), row: 0 })
    furniture.push({ room: 'garage', catalog: 'workbench', where: 'wall' }, { room: 'garage', catalog: 'metal-shelf', where: 'end' })
  }
  const naturalArea = Math.round(rooms.filter((r) => r.kind !== 'garage' && r.kind !== 'outdoor').reduce((s, r) => s + r.w * r.d, 0))
  return { rooms, furniture, naturalArea, garageX: width }
}
